<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MCompanyEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: false,
    },
    target: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getPayload = (...args) => {
    const [editor] = args;
    console.log(editor);
    return {
      id: editor.id,
      etag: editor.getETAG(),
      ...editor.getChanges({ skipEmbeddedLists: true }),
    };
  };

  const formId = ref(null);
  const defaultTitle = ref(
    $fn.value.pgettext('Company', 'Edit General Information')
  );
  const targetConfig = ref({
    general: {
      title: $fn.value.pgettext('Company', 'Edit general data'),
      fieldsToSubmit: ['*'],
    },
  });
  const editor = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    defaultTitle: defaultTitle.value,
    targetConfig: targetConfig.value,
    editor: editor.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_editor = () => {
    return props.item.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'defaultTitle' },
      { name: 'targetConfig' },
      { name: 'editor' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId, editor: $$load_editor },
    ux: {},
    dataRefs: {
      formId,
      defaultTitle,
      targetConfig,
      editor,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.target
          ? targetConfig.value[props.target].title
          : defaultTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ixko1udfb_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_ommnlu0t9_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_gwdpu4cob_getActionArgs = async ($event) => {
    return {
      objectType: 'company',
      op: 'upsert',
      data: getPayload(editor.value),
    };
  };
  const act_2434514588044777_closeModal_70s27b3be_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_ixko1udfb =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ixko1udfb_getActionArgs,
    });
  const inst_evh_7315092382398562_close_4p6dsne0z =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ixko1udfb,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_4p6dsne0z],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_63negwndm =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_obgwi9k56 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_63negwndm,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_obgwi9k56],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_t3clulev5 =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_fmucwlv4h =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_t3clulev5,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_fmucwlv4h],
    event: 'close',
  });
  const act_1040167445267876_closeModal_ommnlu0t9 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_ommnlu0t9_getActionArgs,
    });
  const inst_evh_1040167445267876_success_6lhf0du32 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_ommnlu0t9,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_gwdpu4cob = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_gwdpu4cob_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_6lhf0du32],
  });
  const inst_evh_1040167445267876_submit_1612d9b9e =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_gwdpu4cob,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_1612d9b9e],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_70s27b3be =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_70s27b3be_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_2wdnagj8c =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_70s27b3be,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_2wdnagj8c],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_4p6dsne0z,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_obgwi9k56,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_fmucwlv4h,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_6lhf0du32,
    inst_evh_1040167445267876_submit_1612d9b9e,
    save: inst_evh_1040167445267876_submit_1612d9b9e,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_2wdnagj8c,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_4p6dsne0z = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_4p6dsne0z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_obgwi9k56 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_obgwi9k56.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_fmucwlv4h = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_fmucwlv4h.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_6lhf0du32 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_6lhf0du32.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_1612d9b9e = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_1612d9b9e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_2wdnagj8c = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_2wdnagj8c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MCompanyEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="company"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <MCompanyEditorFieldset
            :item="editor"
            :formId="formId"
            :target="target"
          >
          </MCompanyEditorFieldset>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.company || !forms.company.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
